import { useQueryClient } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core/useDialogs';
import { useGetCourse } from 'api/hooks/private/courses/GetCourse';

import { useUpdateCourseTerms } from 'api/hooks/private/courses/UpdateCourseTerms';
import AgreementForm, { AgreementOutputValues } from 'components/AgreementForm';

import FeedbackDialog from 'components/Dialogs/FeedbackDialog';
import { RichTextEditorProvider } from 'components/Editor/RichTextEditor/RichTextEditorProvider';
import TermsTheme from 'components/Editor/themes/TermsTheme';
import { SerializedEditorState } from 'lexical';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

interface CourseTermsProps {
    courseId: string;
}

export function CourseTerms({ courseId }: CourseTermsProps) {
    const dialogs = useDialogs();
    const queryClient = useQueryClient();
    const updateCourseTerms = useUpdateCourseTerms();

    // fetch the package
    const course = useGetCourse({
        variables: {
            id: courseId,
        },
        enabled: !!courseId,
    });

    const onSubmit = async (data: AgreementOutputValues) => {
        try {
            // show confirmation dialog
            const confirmed = await dialogs.confirm(`Update Course Terms of Service?`, {
                okText: 'Update',
                cancelText: 'Cancel',
            });

            // if user cancels, return
            if (!confirmed) {
                return;
            }

            const result = await updateCourseTerms.mutateAsync({
                courseId: courseId,
                data: {
                    content: data.content as SerializedEditorState
                },
            });

            if (result) {
                // invalidate the query key
                queryClient.invalidateQueries({
                    queryKey: ['course', { id: courseId }],
                });

                // show success message
                dialogs.open(FeedbackDialog, {
                    icon: 'success',
                    title: 'Course Terms Updated',
                });
            }
        } catch (error) {
            dialogs.open(FeedbackDialog, {
                title: 'Error',
                message: getApiErrorMessage(error),
            });
        }
    };

    return (
        <RichTextEditorProvider namespace='course-terms' theme={TermsTheme}>
            <AgreementForm
                initialData={{
                    content: course.data?.agreement?.agreement_versions?.[0].content
                }}
                isLoading={course.isLoading}
                onSubmit={onSubmit}
            />
        </RichTextEditorProvider>

    );
}

export default CourseTerms;
